import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  InputAdornment,
  Divider,
  CircularProgress,
  Switch,
  FormControlLabel,
  Avatar,
  Tooltip,
  TablePagination,
  Autocomplete,
  Snackbar
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  MyLocation,
  Visibility,
  Edit
} from "@mui/icons-material";
// Update the imports to include useSelector and admin from Redux
import { useSelector, useDispatch } from "react-redux";
import { getCurrencySymbolFromSchool } from "./utils/currencyFormatter";
import { useAuth } from "./contexts/AuthContext";
import axiosInstance from "./utils/axios";
import { format } from "date-fns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Redux imports
import {
  setProducts,
  setSales,
  setRestockHistory,
  setLoading,
  setError,
  clearError,
  setSearchTerm,
  setCategoryFilter,
  setCategoryFilterSales,
  setDateFilter,
  setStartDate,
  setEndDate,
  setPaymentMethodFilter,
  setMinAmount,
  setMaxAmount,
  setInventoryPage,
  setInventoryRowsPerPage,
  setPosPage,
  setPosRowsPerPage,
  setSalesPage,
  setSalesRowsPerPage,
  setRestockPage,
  setRestockRowsPerPage,
  setSortBy,
  setSortOrder,
  addToCart,
  updateCartItem,
  removeFromCart,
  clearCart,
  setPaymentMethod,
  setProductDialogOpen,
  setRestockDialogOpen,
  setSaleDetailsDialogOpen,
  setDeleteConfirmOpen,
  setSelectedProduct,
  setSelectedSale,
  setProductToDelete,
  setShowSummary,
  clearFilters
} from "./redux/sales";

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sales-tabpanel-${index}`}
      aria-labelledby={`sales-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Product Form Dialog
const ProductFormDialog = ({ open, onClose, product, onSave }) => {
  // Get currency symbol from school details
  const schooldetails_ = useSelector((state) => state.schooldetails) || [];
  const currencySymbol = getCurrencySymbolFromSchool(schooldetails_);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    buyingPrice: "",
    sellingPrice: "",
    stockQuantity: "",
    minStockLevel: "",
    barcode: "",
    supplier: ""
  });

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || "",
        description: product.description || "",
        category: product.category || "",
        buyingPrice: product.buyingPrice || "",
        sellingPrice: product.sellingPrice || "",
        stockQuantity: product.stockQuantity || "",
        minStockLevel: product.minStockLevel || "",
        barcode: product.barcode || "",
        supplier: product.supplier || ""
      });
    } else {
      setFormData({
        name: "",
        description: "",
        category: "",
        buyingPrice: "",
        sellingPrice: "",
        stockQuantity: "",
        minStockLevel: "",
        barcode: "",
        supplier: ""
      });
    }
  }, [product, open]);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSave = () => {
    // Validate required fields
    if (!formData.name.trim()) {
      alert("Product name is required");
      return;
    }
    
    const validCategories = ["food", "stationery", "uniforms", "books", "electronics", "other"];
    if (!formData.category || !validCategories.includes(formData.category)) {
      alert("Please select a valid category");
      return;
    }
    
    if (!formData.buyingPrice || parseFloat(formData.buyingPrice) <= 0) {
      alert("Buying price must be greater than 0");
      return;
    }
    
    if (!formData.sellingPrice || parseFloat(formData.sellingPrice) <= 0) {
      alert("Selling price must be greater than 0");
      return;
    }
    
    if (!formData.stockQuantity || parseInt(formData.stockQuantity) < 0) {
      alert("Stock quantity must be 0 or greater");
      return;
    }
    
    if (!formData.minStockLevel || parseInt(formData.minStockLevel) < 0) {
      alert("Minimum stock level must be 0 or greater");
      return;
    }
    
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {product ? "Edit Product" : "Add New Product"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Product Name"
              value={formData.name}
              onChange={handleChange("name")}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={handleChange("category")}
                label="Category"
              >
                <MenuItem value="food">Food</MenuItem>
                <MenuItem value="stationery">Stationery</MenuItem>
                <MenuItem value="uniforms">Uniforms</MenuItem>
                <MenuItem value="books">Books</MenuItem>
                <MenuItem value="electronics">Electronics</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`Buying Price (${currencySymbol})`}
              type="number"
              value={formData.buyingPrice}
              onChange={handleChange("buyingPrice")}
              InputProps={{
                startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
              }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`Selling Price (${currencySymbol})`}
              type="number"
              value={formData.sellingPrice}
              onChange={handleChange("sellingPrice")}
              InputProps={{
                startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
              }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Stock Quantity"
              type="number"
              value={formData.stockQuantity}
              onChange={handleChange("stockQuantity")}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Minimum Stock Level"
              type="number"
              value={formData.minStockLevel}
              onChange={handleChange("minStockLevel")}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Barcode"
              value={formData.barcode}
              onChange={handleChange("barcode")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Supplier"
              value={formData.supplier}
              onChange={handleChange("supplier")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange("description")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {product ? "Update" : "Add"} Product
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Update the RestockDialog component to include schoolID
const RestockDialog = ({ open, onClose, product, onRestock, currentUser, schoolID }) => {
  const [quantity, setQuantity] = useState("");
  const [cost, setCost] = useState("");

  useEffect(() => {
    if (product) {
      setCost(product.buyingPrice || "");
    }
  }, [product]);

  // Update the RestockDialog to send the correct field names
  const handleRestock = () => {
    if (!schoolID) {
      alert("School ID is required for restocking");
      return;
    }
    
    onRestock({
      productId: product._id,
      productName: product.name, // Add product name
      quantity: parseInt(quantity),
      costPerUnit: parseFloat(cost), // Change from 'cost' to 'costPerUnit'
      totalCost: parseFloat(cost) * parseInt(quantity),
      restockedBy: currentUser?.schoolname || currentUser?.othernames || currentUser?.lastname || "Current User",
      schoolID: schoolID
    });
    setQuantity("");
    setCost("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Restock Product</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {product?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current Stock: {product?.stockQuantity || 0}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost per Unit"
                type="number"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                required
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleRestock} variant="contained" disabled={!quantity || !cost}>
          Restock
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Cart Item Component
const CartItem = ({ item, onUpdateQuantity, onRemove }) => {
  // Get currency symbol from school details
  const schooldetails_ = useSelector((state) => state.schooldetails) || [];
  const currencySymbol = getCurrencySymbolFromSchool(schooldetails_);
  
  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle2">{item.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {currencySymbol}{item.price.toFixed(2)} × {item.quantity}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          size="small"
          onClick={() => onUpdateQuantity(item.quantity - 1)}
          disabled={item.quantity <= 1}
        >
          -
        </Button>
        <Typography variant="body2">{item.quantity}</Typography>
        <Button
          size="small"
          onClick={() => onUpdateQuantity(item.quantity + 1)}
        >
          +
        </Button>
        <IconButton size="small" onClick={onRemove}>
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

// Add this component to display location information in attendance records
const LocationDisplay = ({ location, showFull = false }) => {
  if (!location) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <MyLocation color="primary" fontSize="small" />
      <Box>
        <Typography variant="caption" color="text.secondary">
          {location.address || `${location.latitude?.toFixed(4)}, ${location.longitude?.toFixed(4)}`}
        </Typography>
        {showFull && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Accuracy: ±{Math.round(location.accuracy)}m
            </Typography>
            {location.distanceFromSchool && (
              <Typography variant="caption" color="text.secondary">
                Distance: {Math.round(location.distanceFromSchool)}m
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Main Sales Management Component
export default function SalesManagement() {
  const dispatch = useDispatch();
  
  // Get currency symbol from school details - define early to ensure it's always available
  const schooldetails_ = useSelector((state) => state.schooldetails) || [];
  const currencySymbol = getCurrencySymbolFromSchool(schooldetails_);
  
  // Get Redux state
  const salesState = useSelector((state) => state.sales) || {
    products: [],
    sales: [],
    restockHistory: [],
    loading: {
      products: false,
      sales: false,
      restockHistory: false,
      checkout: false,
      productAction: false
    },
    error: null,
    searchTerm: '',
    categoryFilter: 'all',
    categoryFilterSales: 'all',
    dateFilter: 'all',
    startDate: null,
    endDate: null,
    paymentMethodFilter: 'all',
    minAmount: '',
    maxAmount: '',
    inventoryPage: 0,
    inventoryRowsPerPage: 10,
    posPage: 0,
    posRowsPerPage: 10,
    salesPage: 0,
    salesRowsPerPage: 10,
    restockPage: 0,
    restockRowsPerPage: 5,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    cart: [],
    paymentMethod: 'cash',
    productDialogOpen: false,
    restockDialogOpen: false,
    saleDetailsDialogOpen: false,
    deleteConfirmOpen: false,
    selectedProduct: null,
    selectedSale: null,
    productToDelete: null,
    showSummary: true
  };

  const {
    products,
    sales,
    restockHistory,
    loading,
    error,
    searchTerm,
    categoryFilter,
    categoryFilterSales,
    dateFilter,
    startDate,
    endDate,
    paymentMethodFilter,
    minAmount,
    maxAmount,
    inventoryPage,
    inventoryRowsPerPage,
    posPage,
    posRowsPerPage,
    salesPage,
    salesRowsPerPage,
    restockPage,
    restockRowsPerPage,
    sortBy,
    sortOrder,
    cart,
    paymentMethod,
    productDialogOpen,
    restockDialogOpen,
    saleDetailsDialogOpen,
    deleteConfirmOpen,
    selectedProduct,
    selectedSale,
    productToDelete,
    showSummary
  } = salesState;

  // Local state for UI-specific features
  const [activeTab, setActiveTab] = useState(0);
  
  // Get current user from AuthContext
  // const { user, admin } = useAuth();
  const admin = useSelector((state) => state.admin);
  const { students } = useSelector((state) => state);

  // Add state for customer information
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [saleNotes, setSaleNotes] = useState("");

  // Add state for student selection
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");

  // Add success notification state
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  // Add success notification functions
  const showSuccessNotification = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMessage("");
  };

  // Fetch data functions
  const fetchProducts = useCallback(async () => {
    if (!admin?.schoolID) return;
    
    try {
      dispatch(setLoading({ products: true }));
      const response = await axiosInstance.get('/api/sales/products', {
        params: {
          schoolID: admin.schoolID
        }
      });
      dispatch(setProducts(response.data.data));
    } catch (error) {
      console.error('Error fetching products:', error);
      dispatch(setError('Failed to fetch products'));
    } finally {
      dispatch(setLoading({ products: false }));
    }
  }, [admin?.schoolID, dispatch]);

  const fetchSales = useCallback(async () => {
    if (!admin?.schoolID) return;
    
    try {
      dispatch(setLoading({ sales: true }));
      const response = await axiosInstance.get('/api/sales/sales', {
        params: {
          schoolID: admin.schoolID
        }
      });
      dispatch(setSales(response.data.data));
    } catch (error) {
      console.error('Error fetching sales:', error);
      dispatch(setError('Failed to fetch sales'));
    } finally {
      dispatch(setLoading({ sales: false }));
    }
  }, [admin?.schoolID, dispatch]);

  const fetchRestockHistory = useCallback(async () => {
    if (!admin?.schoolID) return;
    
    try {
      dispatch(setLoading({ restockHistory: true }));
      const response = await axiosInstance.get('/api/sales/restock', {
        params: {
          schoolID: admin.schoolID
        }
      });
      dispatch(setRestockHistory(response.data.data));
    } catch (error) {
      console.error('Error fetching restock history:', error);
      dispatch(setError('Failed to fetch restock history'));
    } finally {
      dispatch(setLoading({ restockHistory: false }));
    }
  }, [admin?.schoolID, dispatch]);

  // Fetch data on component mount
  useEffect(() => {
    fetchProducts();
    fetchSales();
    fetchRestockHistory();
  }, [fetchProducts, fetchSales, fetchRestockHistory]);

  // Product management functions
  const handleSaveProduct = async (productData) => {
    try {
      dispatch(setLoading({ productAction: true }));
      
      console.log("Original product data:", productData);
      
      // Convert string values to numbers and add required fields
      const processedData = {
        name: productData.name?.trim() || "",
        description: productData.description?.trim() || "",
        category: productData.category?.trim() || "",
        buyingPrice: parseFloat(productData.buyingPrice) || 0,
        sellingPrice: parseFloat(productData.sellingPrice) || 0,
        stockQuantity: parseInt(productData.stockQuantity) || 0,
        minStockLevel: parseInt(productData.minStockLevel) || 5,
        barcode: productData.barcode?.trim() || undefined,
        supplier: productData.supplier?.trim() || "",
        createdBy: admin?.schoolname || admin?.othernames || admin?.lastname || "System",
        updatedBy: admin?.schoolname || admin?.othernames || admin?.lastname || "System",
        schoolID: admin?.schoolID
      };
      
      console.log("Processed product data:", processedData);
      
      // Validate required fields
      if (!processedData.name) {
        dispatch(setError("Product name is required"));
        return;
      }
      
      if (!processedData.category) {
        dispatch(setError("Product category is required"));
        return;
      }
      
      const validCategories = ["food", "stationery", "uniforms", "books", "electronics", "other"];
      if (!validCategories.includes(processedData.category)) {
        dispatch(setError("Please select a valid category"));
        return;
      }
      
      if (processedData.buyingPrice <= 0) {
        dispatch(setError("Buying price must be greater than 0"));
        return;
      }
      
      if (processedData.sellingPrice <= 0) {
        dispatch(setError("Selling price must be greater than 0"));
        return;
      }
      
      if (processedData.stockQuantity < 0) {
        dispatch(setError("Stock quantity must be 0 or greater"));
        return;
      }
      
      if (processedData.minStockLevel < 0) {
        dispatch(setError("Minimum stock level must be 0 or greater"));
        return;
      }
      
      if (!processedData.schoolID) {
        dispatch(setError("School ID is required"));
        return;
      }
      
      if (selectedProduct) {
        // Update existing product
        console.log("Updating product:", selectedProduct._id);
        const response = await axiosInstance.put(`/api/sales/products/${selectedProduct._id}`, processedData);
        console.log("Update response:", response.data);
        const updatedProducts = products && products.map(p => 
          p._id === selectedProduct._id ? response.data.data : p
        );
        dispatch(setProducts(updatedProducts));
      } else {
        // Create new product
        console.log("Creating new product");
        const response = await axiosInstance.post('/api/sales/products', processedData);
        console.log("Create response:", response.data);
        dispatch(setProducts([...products, response.data.data]));
      }
      
      dispatch(setProductDialogOpen(false));
      dispatch(setSelectedProduct(null));
      dispatch(clearError());
    } catch (error) {
      console.error("Error saving product:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      let errorMessage = "Failed to save product";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors.join(", ");
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch(setError(errorMessage));
    } finally {
      dispatch(setLoading({ productAction: false }));
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      dispatch(setLoading({ productAction: true }));
      await axiosInstance.delete(`/api/sales/products/${productId}`);
      const updatedProducts = products.filter(p => p._id !== productId);
      dispatch(setProducts(updatedProducts));
      dispatch(setDeleteConfirmOpen(false));
      dispatch(setProductToDelete(null));
    } catch (error) {
      dispatch(setError(error.response?.data?.message || 'Failed to delete product'));
    } finally {
      dispatch(setLoading({ productAction: false }));
    }
  };

  // Update the handleRestock function to include schoolID validation
  const handleRestock = async (restockData) => {
    try {
      dispatch(setLoading({ productAction: true }));
      
      // Validate schoolID
      if (!restockData.schoolID) {
        dispatch(setError("School ID is required for restocking"));
        return;
      }
      
      console.log("Restock data:", restockData);
      
      const response = await axiosInstance.post('/api/sales/restock', restockData);
      
      // Update product stock
      const updatedProducts = products && products.map(p => 
        p._id === restockData.productId 
          ? { ...p, stockQuantity: p.stockQuantity + restockData.quantity }
          : p
      );
      dispatch(setProducts(updatedProducts));
      
      // Add to restock history
      dispatch(setRestockHistory([...restockHistory, response.data.data]));
      
      dispatch(setRestockDialogOpen(false));
      dispatch(clearError());
    } catch (error) {
      console.error("Error restocking product:", error);
      dispatch(setError(error.response?.data?.message || 'Failed to restock product'));
    } finally {
      dispatch(setLoading({ productAction: false }));
    }
  };

  // POS functions
  const handleAddToCart = (product) => {
    dispatch(addToCart(product));
    showSuccessNotification(`${product.name} added to cart`);
  };

  const handleUpdateCartItem = (index, newQuantity) => {
    const item = cart[index];
    if (newQuantity === 0) {
      dispatch(removeFromCart(index));
      showSuccessNotification(`${item.name} removed from cart`);
    } else {
      dispatch(updateCartItem({ index, quantity: newQuantity }));
      showSuccessNotification(`${item.name} quantity updated to ${newQuantity}`);
    }
  };

  const handleRemoveFromCart = (index) => {
    const item = cart[index];
    dispatch(removeFromCart(index));
    showSuccessNotification(`${item.name} removed from cart`);
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Update the handleCheckout function to use Snackbar notifications
  const handleCheckout = async () => {
    if (cart.length === 0) {
      dispatch(setError("Cart is empty"));
      return;
    }

    if (!admin?.schoolID) {
      dispatch(setError("School ID is required for processing sales"));
      return;
    }

    try {
      dispatch(setLoading({ checkout: true }));
      
      // Validate stock availability before processing
      for (const cartItem of cart) {
        const product = products.find(p => p._id === cartItem.productId);
        if (!product) {
          dispatch(setError(`Product ${cartItem.name} not found`));
          return;
        }
        
        if (product.stockQuantity < cartItem.quantity) {
          dispatch(setError(`Insufficient stock for ${cartItem.name}. Available: ${product.stockQuantity}`));
          return;
        }
      }
      
      // Determine customer information
      let finalCustomerName = "Walk-in Customer";
      let finalCustomerPhone = "";
      
      if (selectedStudent) {
        finalCustomerName = `${selectedStudent.othernames} ${selectedStudent.lastname}`;
        finalCustomerPhone = selectedStudent.phone || selectedStudent.guardianPhone || "";
      } else if (customerName.trim()) {
        finalCustomerName = customerName.trim();
        finalCustomerPhone = customerPhone.trim();
      }
      
      const saleData = {
        schoolID: admin.schoolID,
        items: cart.map(item => ({
          productId: item.productId,
          productName: item.name,
          quantity: item.quantity,
          unitPrice: item.price,
          totalPrice: item.price * item.quantity
        })),
        customerName: finalCustomerName,
        customerPhone: finalCustomerPhone,
        subtotal: cart.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        discount: 0,
        tax: 0,
        total: cart.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        paymentMethod: paymentMethod || "cash",
        paymentStatus: "paid",
        cashier: admin?.schoolname || admin?.othernames || admin?.lastname || "Unknown",
        notes: saleNotes.trim() || (selectedStudent ? `Student: ${selectedStudent.studentID || selectedStudent._id}` : "")
      };

      console.log("Processing sale with data:", saleData);

      const response = await axiosInstance.post('/api/sales/sales', saleData);
      
      console.log("Sale processed successfully:", response.data);
      
      // Add the new sale to the sales list
      dispatch(setSales([...sales, response.data.data]));
      
      // Update product stock quantities
      const updatedProducts = products.map(product => {
        const cartItem = cart.find(item => item.productId === product._id);
        if (cartItem) {
          return {
            ...product,
            stockQuantity: product.stockQuantity - cartItem.quantity
          };
        }
        return product;
      });
      dispatch(setProducts(updatedProducts));
      
      // Clear cart and form
      dispatch(clearCart());
      setCustomerName("");
      setCustomerPhone("");
      setSaleNotes("");
      setSelectedStudent(null);
      setStudentSearchTerm("");
      
      // Clear any existing errors and show success
      dispatch(setError(""));
      showSuccessNotification(`Sale processed successfully! Sale Number: ${response.data.data.saleNumber}`);
      
    } catch (error) {
      console.error("Error processing checkout:", error);
      console.error("Error response:", error.response?.data);
      
      let errorMessage = "Failed to process checkout";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch(setError(errorMessage));
    } finally {
      dispatch(setLoading({ checkout: false }));
    }
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.barcode?.includes(searchTerm);
    const matchesCategory = categoryFilter === "all" || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Enhanced filtering function
  const getFilteredSales = () => {
    let filtered = [...sales];

    // Date filtering
    if (dateFilter === "today") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      filtered = filtered.filter(sale => {
        const saleDate = new Date(sale.createdAt);
        return saleDate >= today && saleDate < tomorrow;
      });
    } else if (dateFilter === "week") {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(sale => new Date(sale.createdAt) >= weekAgo);
    } else if (dateFilter === "month") {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      filtered = filtered.filter(sale => new Date(sale.createdAt) >= monthAgo);
    } else if (dateFilter === "custom" && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(sale => {
        const saleDate = new Date(sale.createdAt);
        return saleDate >= start && saleDate <= end;
      });
    }

    // Payment method filtering
    if (paymentMethodFilter !== "all") {
      filtered = filtered.filter(sale => sale.paymentMethod === paymentMethodFilter);
    }

    // Amount range filtering
    if (minAmount) {
      filtered = filtered.filter(sale => sale.total >= parseFloat(minAmount));
    }
    if (maxAmount) {
      filtered = filtered.filter(sale => sale.total <= parseFloat(maxAmount));
    }

    // Category filtering for sales
    if (categoryFilterSales !== "all") {
      filtered = filtered.filter(sale => 
        sale.items.some(item => {
          const product = products.find(p => p._id === item.productId);
          return product && product.category === categoryFilterSales;
        })
      );
    }

    return filtered;
  };

  // Calculate summary statistics
  const getSummaryStats = (filteredSales) => {
    const totalSales = filteredSales.length;
    const totalRevenue = filteredSales.reduce((sum, sale) => sum + sale.total, 0);
    const highestSale = totalSales > 0 ? Math.max(...filteredSales && filteredSales.map(s => s.total)) : 0;
    const lowestSale = totalSales > 0 ? Math.min(...filteredSales && filteredSales.map(s => s.total)) : 0;

    // Calculate profit
    const totalProfit = filteredSales.reduce((sum, sale) => {
      const saleProfit = sale.items.reduce((itemSum, item) => {
        const product = products.find(p => p._id === item.productId);
        if (product && product.buyingPrice && !isNaN(product.buyingPrice) && !isNaN(item.unitPrice)) {
          const profitPerItem = (item.unitPrice - product.buyingPrice) * item.quantity;
          return itemSum + (isNaN(profitPerItem) ? 0 : profitPerItem);
        }
        return itemSum;
      }, 0);
      return sum + (isNaN(saleProfit) ? 0 : saleProfit);
    }, 0);

    return {
      totalSales,
      totalRevenue,
      highestSale,
      lowestSale,
      totalProfit
    };
  };

  // Calculate statistics
  const totalProducts = products.length;
  const lowStockProducts = products.filter(p => p.stockQuantity <= p.minStockLevel).length;
  const totalSalesAmount = sales.reduce((sum, sale) => sum + sale.total, 0);
  const todaySales = sales.filter(sale => 
    new Date(sale.createdAt).toDateString() === new Date().toDateString()
  ).reduce((sum, sale) => sum + sale.total, 0);

  // Additional statistics for sorted data
  const averageSale = sales.length > 0 ? totalSalesAmount / sales.length : 0;
  const highestSale = sales.length > 0 ? Math.max(...sales && sales.map(s => s.total)) : 0;
  const lowestSale = sales.length > 0 ? Math.min(...sales && sales.map(s => s.total)) : 0;

  // Get paginated data
  const getPaginatedInventory = () => {
    const startIndex = inventoryPage * inventoryRowsPerPage;
    return filteredProducts.slice(startIndex, startIndex + inventoryRowsPerPage);
  };

  const getPaginatedPos = () => {
    const startIndex = posPage * posRowsPerPage;
    return filteredProducts.slice(startIndex, startIndex + posRowsPerPage);
  };

  const getPaginatedSales = () => {
    const startIndex = salesPage * salesRowsPerPage;
    return getFilteredSales().slice(startIndex, startIndex + salesRowsPerPage);
  };

  const getPaginatedRestock = () => {
    const startIndex = restockPage * restockRowsPerPage;
    return restockHistory.slice(startIndex, startIndex + restockRowsPerPage);
  };

  // Sales history actions
  const handleViewSaleDetails = (sale) => {
    dispatch(setSelectedSale(sale));
    dispatch(setSaleDetailsDialogOpen(true));
  };

  const handlePrintReceipt = (sale) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    const receiptContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${sale._id}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
            .receipt-info { margin-bottom: 20px; }
            .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .items-table th, .items-table td { border: 1px solid #000; padding: 8px; text-align: left; }
            .items-table th { background-color: #f0f0f0; }
            .total { font-size: 18px; font-weight: bold; text-align: right; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Sales Receipt</h2>
            <p>Receipt #: ${sale._id}</p>
          </div>
          
          <div class="receipt-info">
            <p><strong>Date:</strong> ${format(new Date(sale.createdAt), "MMM dd, yyyy HH:mm")}</p>
            <p><strong>Customer:</strong> ${sale.customerName || "Walk-in Customer"}</p>
            <p><strong>Cashier:</strong> ${sale.cashier}</p>
            <p><strong>Payment Method:</strong> ${sale.paymentMethod.replace('_', ' ').toUpperCase()}</p>
          </div>
          
          <table class="items-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Price</th>
                <th>Qty</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${sale.items.map(item => `
                <tr>
                  <td>${item.productName || 'Unknown Item'}</td>
                  <td>${currencySymbol}${(item.unitPrice || 0).toFixed(2)}</td>
                  <td>${item.quantity || 0}</td>
                  <td>${currencySymbol}${((item.unitPrice || 0) * (item.quantity || 0)).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="total">
            <p>Total: ${currencySymbol}${(sale.total || 0).toFixed(2)}</p>
          </div>
          
          <div class="footer">
            <p>Thank you for your business!</p>
            <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Pagination handlers for all tables
  const handleInventoryPageChange = (event, newPage) => {
    dispatch(setInventoryPage(newPage));
  };

  const handleInventoryRowsPerPageChange = (event) => {
    dispatch(setInventoryRowsPerPage(parseInt(event.target.value, 10)));
  };

  const handlePosPageChange = (event, newPage) => {
    dispatch(setPosPage(newPage));
  };

  const handlePosRowsPerPageChange = (event) => {
    dispatch(setPosRowsPerPage(parseInt(event.target.value, 10)));
  };

  const handleSalesPageChange = (event, newPage) => {
    dispatch(setSalesPage(newPage));
  };

  const handleSalesRowsPerPageChange = (event) => {
    dispatch(setSalesRowsPerPage(parseInt(event.target.value, 10)));
  };

  const handleRestockPageChange = (event, newPage) => {
    dispatch(setRestockPage(newPage));
  };

  const handleRestockRowsPerPageChange = (event) => {
    dispatch(setRestockRowsPerPage(parseInt(event.target.value, 10)));
  };

  // Delete confirmation handlers
  const handleDeleteClick = (product) => {
    dispatch(setProductToDelete(product));
    dispatch(setDeleteConfirmOpen(true));
  };

  const handleDeleteConfirm = async () => {
    if (productToDelete) {
      dispatch(setDeleteConfirmOpen(false));
      dispatch(setProductToDelete(null));
    }
  };

  const handleDeleteCancel = () => {
    dispatch(setDeleteConfirmOpen(false));
    dispatch(setProductToDelete(null));
  };

  // Show loading or error if store is not available
  if (!salesState) { // Changed from isStoreAvailable to salesState
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Redux store is not available. Please check your store configuration.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          🛒 Sales Management System
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => dispatch(setProductDialogOpen(true))}
        >
          Add Product
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InventoryIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{totalProducts}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Products
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <WarningIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{lowStockProducts}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Low Stock
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircleIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{currencySymbol}{todaySales.toFixed(2)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Today's Sales
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{currencySymbol}{totalSalesAmount.toFixed(2)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Sales
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <MoneyIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{currencySymbol}{averageSale.toFixed(2)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Sale
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{currencySymbol}{highestSale.toFixed(2)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Highest Sale
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="secondary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{currencySymbol}{lowestSale.toFixed(2)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Lowest Sale
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <HistoryIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{sales.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Transactions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab icon={<InventoryIcon />} label="Inventory" />
            <Tab icon={<ShoppingCartIcon />} label="Point of Sale" />
            <Tab icon={<HistoryIcon />} label="Sales History" />
            <Tab icon={<ShippingIcon />} label="Restock History" />
          </Tabs>
        </Box>

        {/* Inventory Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => dispatch(setSearchTerm(e.target.value))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => dispatch(setCategoryFilter(e.target.value))}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="food">Food</MenuItem>
                <MenuItem value="stationery">Stationery</MenuItem>
                <MenuItem value="electronics">Electronics</MenuItem>
                <MenuItem value="clothing">Clothing</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Buying Price</TableCell>
                  <TableCell>Selling Price</TableCell>
                  <TableCell>Stock Quantity</TableCell>
                  <TableCell>Min Stock Level</TableCell>
                  <TableCell>Barcode</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getPaginatedInventory().map((product) => (
                  <TableRow key={product._id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                      {product.name}
                    </Typography>
                        {product.description && (
                    <Typography variant="body2" color="text.secondary">
                            {product.description}
                    </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={product.category} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{currencySymbol}{product.buyingPrice?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" color="primary">
                        {currencySymbol}{product.sellingPrice?.toFixed(2) || '0.00'}
                    </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={product.stockQuantity <= product.minStockLevel ? 'error' : 'text.primary'}
                      >
                        {product.stockQuantity || 0}
                    </Typography>
                    </TableCell>
                    <TableCell>{product.minStockLevel || 0}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {product.barcode || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {product.supplier || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={product.stockQuantity <= product.minStockLevel ? 'Low Stock' : 'In Stock'} 
                        size="small"
                        color={product.stockQuantity <= product.minStockLevel ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton 
                          size="small" 
                        onClick={() => {
                            dispatch(setSelectedProduct(product));
                            dispatch(setProductDialogOpen(true));
                          }}
                          title="Edit Product"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                        size="small"
                        onClick={() => {
                            dispatch(setSelectedProduct(product));
                            dispatch(setRestockDialogOpen(true));
                          }}
                          title="Restock Product"
                          color="warning"
                        >
                          <ShippingIcon />
                        </IconButton>
                        <IconButton 
                        size="small"
                          onClick={() => handleDeleteClick(product)}
                          title="Delete Product"
                        color="error"
                      >
                          <DeleteIcon />
                        </IconButton>
                    </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Inventory Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredProducts.length}
            rowsPerPage={inventoryRowsPerPage}
            page={inventoryPage}
            onPageChange={handleInventoryPageChange}
            onRowsPerPageChange={handleInventoryRowsPerPageChange}
          />
        </TabPanel>

        {/* Point of Sale Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            {/* Products Table */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Products
                  </Typography>
                    <TextField
                      size="small"
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => dispatch(setSearchTerm(e.target.value))}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={categoryFilter}
                        onChange={(e) => dispatch(setCategoryFilter(e.target.value))}
                        label="Category"
                      >
                        <MenuItem value="all">All Categories</MenuItem>
                        <MenuItem value="food">Food</MenuItem>
                        <MenuItem value="stationery">Stationery</MenuItem>
                        <MenuItem value="electronics">Electronics</MenuItem>
                        <MenuItem value="clothing">Clothing</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Stock</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getPaginatedPos().map((product) => (
                          <TableRow 
                            key={product._id}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'action.hover' },
                              '&:last-child td, &:last-child th': { border: 0 }
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography variant="subtitle2">
                              {product.name}
                            </Typography>
                                {product.description && (
                            <Typography variant="body2" color="text.secondary">
                                    {product.description}
                            </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={product.category} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" color="primary">
                                {currencySymbol}{product.sellingPrice?.toFixed(2) || '0.00'}
                            </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                color={product.stockQuantity <= product.minStockLevel ? 'error' : 'text.primary'}
                              >
                                {product.stockQuantity || 0}
                            </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={product.stockQuantity <= product.minStockLevel ? 'Low Stock' : 'Available'} 
                                size="small" 
                                color={product.stockQuantity <= product.minStockLevel ? 'warning' : 'success'}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddToCart(product)}
                                disabled={product.stockQuantity <= 0}
                                fullWidth
                              >
                                Add to Cart
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* POS Pagination */}
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredProducts.length}
                    rowsPerPage={posRowsPerPage}
                    page={posPage}
                    onPageChange={handlePosPageChange}
                    onRowsPerPageChange={handlePosRowsPerPageChange}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Cart */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cart ({cart.length})
                  </Typography>
                  
                  {/* Customer Information */}
                  {cart.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Customer Information
                      </Typography>
                      
                      {/* Student Selection */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Select Student (Optional)
                        </Typography>
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={students || []}
                          getOptionLabel={(option) => option ? `${option.othernames} ${option.lastname} (${option.studentID || option._id})` : ''}
                          value={selectedStudent}
                          onChange={(event, newValue) => {
                            setSelectedStudent(newValue);
                            if (newValue) {
                              setCustomerName(`${newValue.othernames} ${newValue.lastname}`);
                              setCustomerPhone(newValue.phone || newValue.guardianPhone || "");
                            } else {
                              setCustomerName("");
                              setCustomerPhone("");
                            }
                          }}
                          inputValue={studentSearchTerm}
                          onInputChange={(event, newInputValue) => {
                            setStudentSearchTerm(newInputValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search for student..."
                              variant="outlined"
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {option.othernames} {option.lastname}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {option.studentID || option._id} | Class: {option.class || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          isOptionEqualToValue={(option, value) => option?._id === value?._id}
                          noOptionsText="No students found"
                          clearOnEscape
                          selectOnFocus
                          handleHomeEndKeys
                        />
                      </Box>
                      
                      {/* Manual Customer Information */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Or Enter Customer Details Manually
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Customer Name"
                              value={customerName}
                              onChange={(e) => {
                                setCustomerName(e.target.value);
                                // Clear student selection if manually typing
                                if (selectedStudent) {
                                  setSelectedStudent(null);
                                }
                              }}
                              placeholder="Walk-in Customer"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Customer Phone"
                              value={customerPhone}
                              onChange={(e) => {
                                setCustomerPhone(e.target.value);
                                // Clear student selection if manually typing
                                if (selectedStudent) {
                                  setSelectedStudent(null);
                                }
                              }}
                              placeholder="Phone number"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      
                      {/* Notes */}
                      <TextField
                        fullWidth
                        size="small"
                        label="Notes (Optional)"
                        value={saleNotes}
                        onChange={(e) => setSaleNotes(e.target.value)}
                        placeholder="Additional notes"
                        multiline
                        rows={2}
                        sx={{ mb: 2 }}
                      />
                      
                      {/* Clear Selection Button */}
                      {selectedStudent && (
                        <Box sx={{ mb: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedStudent(null);
                              setCustomerName("");
                              setCustomerPhone("");
                            }}
                          >
                            Clear Student Selection
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {/* Payment Information */}
                  {cart.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Payment Information
                      </Typography>
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>Payment Method</InputLabel>
                        <Select
                          value={paymentMethod}
                          onChange={(e) => dispatch(setPaymentMethod(e.target.value))}
                          label="Payment Method"
                        >
                          <MenuItem value="cash">Cash</MenuItem>
                          <MenuItem value="mobile_money">Mobile Money</MenuItem>
                          <MenuItem value="card">Card</MenuItem>
                          <MenuItem value="credit">Credit</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  
                  {cart.length === 0 ? (
                    <Typography color="text.secondary">
                      Cart is empty
                    </Typography>
                  ) : (
                    <Box>
                      {cart && cart.map((item, index) => (
                        <CartItem
                          key={index}
                          item={item}
                          onUpdateQuantity={(newQuantity) => handleUpdateCartItem(index, newQuantity)}
                          onRemove={() => handleRemoveFromCart(index)}
                        />
                      ))}
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                          Total: {currencySymbol}{calculateTotal().toFixed(2)}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleCheckout}
                          disabled={cart.length === 0 || loading.checkout}
                          startIcon={loading.checkout ? <CircularProgress size={20} /> : null}
                        >
                          {loading.checkout ? "Processing..." : "Process Sale"}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Enhanced Sales History Tab */}
        <TabPanel value={activeTab} index={2}>
          {/* Enhanced Filter Controls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🔍 Advanced Filters
              </Typography>
              <Grid container spacing={2}>
                {/* Date Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Date Range</InputLabel>
              <Select
                      value={dateFilter}
                      onChange={(e) => dispatch(setDateFilter(e.target.value))}
                      label="Date Range"
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="week">Last 7 Days</MenuItem>
                      <MenuItem value="month">Last 30 Days</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
                </Grid>

                {/* Custom Date Range */}
                {dateFilter === "custom" && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => dispatch(setStartDate(newValue))}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => dispatch(setEndDate(newValue))}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Grid>
                  </LocalizationProvider>
                )}

                {/* Payment Method Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Method</InputLabel>
              <Select
                      value={paymentMethodFilter}
                      onChange={(e) => dispatch(setPaymentMethodFilter(e.target.value))}
                      label="Payment Method"
                    >
                      <MenuItem value="all">All Methods</MenuItem>
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="mobile_money">Mobile Money</MenuItem>
                      <MenuItem value="card">Card</MenuItem>
                      <MenuItem value="credit">Credit</MenuItem>
              </Select>
            </FormControl>
                </Grid>

                {/* Category Filter for Sales */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Product Category</InputLabel>
                    <Select
                      value={categoryFilterSales}
                      onChange={(e) => dispatch(setCategoryFilterSales(e.target.value))}
                      label="Product Category"
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      <MenuItem value="food">Food</MenuItem>
                      <MenuItem value="stationery">Stationery</MenuItem>
                      <MenuItem value="electronics">Electronics</MenuItem>
                      <MenuItem value="clothing">Clothing</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Amount Range */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Min Amount (${currencySymbol})`}
                    type="number"
                    value={minAmount}
                    onChange={(e) => dispatch(setMinAmount(e.target.value))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Max Amount (${currencySymbol})`}
                    type="number"
                    value={maxAmount}
                    onChange={(e) => dispatch(setMaxAmount(e.target.value))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
                    }}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        dispatch(setDateFilter("all"));
                        dispatch(setStartDate(null));
                        dispatch(setEndDate(null));
                        dispatch(setPaymentMethodFilter("all"));
                        dispatch(setCategoryFilterSales("all"));
                        dispatch(setMinAmount(""));
                        dispatch(setMaxAmount(""));
                      }}
                    >
                      Clear Filters
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PrintIcon />}
                      onClick={() => window.print()}
                    >
                      Print
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          {showSummary && (() => {
            const filteredSales = getFilteredSales();
            const stats = getSummaryStats(filteredSales);
            
            return (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                        <Box>
                          <Typography variant="h6">{stats.totalSales}</Typography>
            <Typography variant="body2" color="text.secondary">
                            Total Sales
            </Typography>
          </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <MoneyIcon color="success" sx={{ mr: 2 }} />
                        <Box>
                          <Typography variant="h6">{currencySymbol}{stats.totalRevenue.toFixed(2)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Revenue
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <TrendingUpIcon color="info" sx={{ mr: 2 }} />
                        <Box>
                          <Typography variant="h6">{currencySymbol}{stats.totalProfit.toFixed(2)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Profit
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <TrendingUpIcon color="primary" sx={{ mr: 2 }} />
                        <Box>
                          <Typography variant="h6">{currencySymbol}{stats.highestSale.toFixed(2)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Highest Sale
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <TrendingUpIcon color="secondary" sx={{ mr: 2 }} />
                        <Box>
                          <Typography variant="h6">{currencySymbol}{stats.lowestSale.toFixed(2)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Lowest Sale
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            );
          })()}

          {/* Results Count and Toggle */}
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {getFilteredSales().length} sales found
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showSummary}
                  onChange={(e) => dispatch(setShowSummary(e.target.checked))}
                />
              }
              label="Show Summary"
            />
          </Box>

          {/* Enhanced Sales Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Cashier</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getPaginatedSales().map((sale) => (
                  <TableRow key={sale._id}>
                    <TableCell>
                      {format(new Date(sale.createdAt), "MMM dd, yyyy HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {sale.items.map(item => item.productName).join(', ')}
                        </Typography>
                        {sale.customerName && (
                        <Typography variant="body2" color="text.secondary">
                            Customer: {sale.customerName}
                        </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={sale.items.map(item => `${item.productName} (${item.quantity})`).join(', ')}>
                        <Typography variant="body2">
                          {sale.items.length} items
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={sale.paymentMethod.replace('_', ' ')} 
                        size="small" 
                        color={
                          sale.paymentMethod === 'cash' ? 'success' :
                          sale.paymentMethod === 'mobile_money' ? 'primary' :
                          sale.paymentMethod === 'card' ? 'info' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" color="primary">
                        {currencySymbol}{sale.total.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>{sale.cashier}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton 
                          size="small" 
                          title="Print Receipt"
                          onClick={() => handlePrintReceipt(sale)}
                        >
                        <PrintIcon />
                      </IconButton>
                        <IconButton 
                          size="small" 
                          title="View Details"
                          onClick={() => handleViewSaleDetails(sale)}
                        >
                          <Visibility />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Sales History Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={getFilteredSales().length}
            rowsPerPage={salesRowsPerPage}
            page={salesPage}
            onPageChange={handleSalesPageChange}
            onRowsPerPageChange={handleSalesRowsPerPageChange}
          />
        </TabPanel>

        {/* Restock History Tab */}
        <TabPanel value={activeTab} index={3}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Cost per Unit</TableCell>
                  <TableCell>Total Cost</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Restocked By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getPaginatedRestock().map((restock) => (
                  <TableRow key={restock._id}>
                    <TableCell>
                      {format(new Date(restock.createdAt), "MMM dd, yyyy HH:mm")}
                    </TableCell>
                    <TableCell>{restock.productName}</TableCell>
                    <TableCell>{restock.quantity}</TableCell>
                    <TableCell>{currencySymbol}{restock.costPerUnit.toFixed(2)}</TableCell>
                    <TableCell>{currencySymbol}{restock.totalCost.toFixed(2)}</TableCell>
                    <TableCell>{restock.supplier}</TableCell>
                    <TableCell>{restock.restockedBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Restock Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={restockHistory.length}
            rowsPerPage={restockRowsPerPage}
            page={restockPage}
            onPageChange={handleRestockPageChange}
            onRowsPerPageChange={handleRestockRowsPerPageChange}
          />
        </TabPanel>
      </Card>

      {/* Dialogs */}
      <ProductFormDialog
        open={productDialogOpen}
        onClose={() => dispatch(setProductDialogOpen(false))}
        product={selectedProduct}
        onSave={handleSaveProduct}
      />

      {/* Update the RestockDialog usage to pass schoolID */}
      <RestockDialog
        open={restockDialogOpen}
        onClose={() => dispatch(setRestockDialogOpen(false))}
        product={selectedProduct}
        onRestock={handleRestock}
        currentUser={admin.schoolID}
        schoolID={admin?.schoolID}
      />

      {/* Sale Details Dialog */}
      <Dialog open={saleDetailsDialogOpen} onClose={() => dispatch(setSaleDetailsDialogOpen(false))} maxWidth="md" fullWidth>
        <DialogTitle>
          Sale Details - {selectedSale?._id}
        </DialogTitle>
        <DialogContent>
          {selectedSale && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedSale.createdAt), "MMM dd, yyyy HH:mm")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
                  <Typography variant="body1">
                    {selectedSale.customerName || "Walk-in Customer"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Cashier</Typography>
                  <Typography variant="body1">{selectedSale.cashier}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {selectedSale.paymentMethod.replace('_', ' ')}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>Items</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedSale.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.productName || 'Unknown Item'}</TableCell>
                        <TableCell>{currencySymbol}{(item.unitPrice || 0).toFixed(2)}</TableCell>
                        <TableCell>{item.quantity || 0}</TableCell>
                        <TableCell>{currencySymbol}{((item.unitPrice || 0) * (item.quantity || 0)).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography variant="h6">
                  Total: {currencySymbol}{(selectedSale.total || 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dispatch(setSaleDetailsDialogOpen(false))}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={() => {
              handlePrintReceipt(selectedSale);
              dispatch(setSaleDetailsDialogOpen(false));
            }}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WarningIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
            <Box>
              <Typography variant="h6">
                Delete Product
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
          {productToDelete && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this product?
              </Typography>
              <Card sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" gutterBottom>
                  {productToDelete.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Category: {productToDelete.category}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Price: {currencySymbol}{productToDelete.sellingPrice?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Stock: {productToDelete.stockQuantity || 0}
                </Typography>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => dispatch(setError(""))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => dispatch(setError(""))} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSuccess} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
